// @flow

import * as React from "react";
import axios from 'axios';
import SimpleModal from "./SimpleModal.react";
import LoadingScreen from "./LoadingScreen.react";
import setupGuideIllustration from "../images/setup_guide_illustration.png";
import {GridIcon} from "./Icons.react";
import {isQuickBooksConnected, showGenericErrorToast} from "../Utils";
import FrozenSubscriptionBanner from "./FrozenSubscriptionBanner.react";
import ReviewPromptModal, {
    crispSaysReviewed, crispSessionLoaded, isEligibleForReviewPrompt,
    markReviewClickedThisSession, reviewClickedThisSession,
} from "./ReviewPromptModal.react";
import {APP_STORE_REVIEW_URL, CRISP_WAIT_MS} from "../Constants";
import withReactContent from 'sweetalert2-react-content'
import Swal from 'sweetalert2'

const swalInstance = withReactContent(Swal)

const DONUT_COLORS = ["#7c5cff", "#f5a623", "#3d7dfc", "#224514"];
// "Other type" (invoices from before the QR type was stored) is always grey, never one of the real types' colors.
const OTHER_TYPE_LABEL = "Other type";
function donutColor(segment, index) {
    return segment.label === OTHER_TYPE_LABEL ? "#c3cad3" : DONUT_COLORS[index % DONUT_COLORS.length];
}

const REVIEW_PROBLEM_MESSAGE = "Hi, I have a problem with Scan2Pay.";

// Step 4 (the review) isn't shown until Crisp's session has loaded (or CRISP_WAIT_MS has passed), so it appears once, in
// its final state: no buttons that then turn into a check.
const SETUP_STEP_COUNT = 4;

function Donut({segments}) {
    const total = segments.reduce((sum, s) => sum + s.count, 0);
    if (total === 0) {
        return <div style={{textAlign: "center", color: "#8892a0", padding: "40px 0"}}>No QR invoices generated yet</div>;
    }

    let cumulative = 0;
    const radius = 60;
    const circumference = 2 * Math.PI * radius;
    return (
        <div style={{display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap"}}>
            <svg width="120" height="120" viewBox="0 0 160 160" style={{flexShrink: 0}}>
                {segments.map((segment, index) => {
                    const fraction = segment.count / total;
                    const dashArray = `${fraction * circumference} ${circumference}`;
                    const dashOffset = -cumulative * circumference;
                    cumulative += fraction;
                    return (
                        <circle key={segment.label} cx="80" cy="80" r={radius} fill="none"
                                stroke={donutColor(segment, index)} strokeWidth="24"
                                strokeDasharray={dashArray} strokeDashoffset={dashOffset}
                                transform="rotate(-90 80 80)" />
                    );
                })}
                <text x="80" y="76" textAnchor="middle" fontSize="28" fontWeight="700" fill="#232b3d">{total}</text>
                <text x="80" y="96" textAnchor="middle" fontSize="12" fill="#8892a0">{total === 1 ? "invoice" : "invoices"}</text>
            </svg>
            <div style={{minWidth: 0}}>
                {segments.map((segment, index) => (
                    <div className="donut-legend-item" key={segment.label} style={{whiteSpace: "nowrap"}}>
                        <span className="donut-legend-dot" style={{background: donutColor(segment, index)}}></span>
                        <span>{segment.label}</span>&nbsp;
                        <b>{Math.round((segment.count / total) * 100)}%</b>
                    </div>
                ))}
            </div>
        </div>
    );
}

class Home extends React.Component {

    constructor() {
        super();
        this.state = {
            isLoading: true,
            totalEntries: 0,
            remainingQrInvoicesForThisMonth: 0,
            planMaxQrInvoicesForMonth: 0,
            planName: '',
            userSettings: {},
            qrTypeBreakdown: [],
            showOnboardingModal: (localStorage.getItem("onboardingModalDismissed") === null),
            crispSessionKnown: crispSessionLoaded(),
            crispSaysReviewed: crispSaysReviewed(),
            problemReported: false,
            reviewClicked: false,
        };

        this.dismissOnboardingModal = this.dismissOnboardingModal.bind(this);
        this.reportProblem = this.reportProblem.bind(this);
        this.markReviewClicked = this.markReviewClicked.bind(this);
    }

    // "Amazing!" opens the App Store review page (the link itself) and marks the step done for this session.
    markReviewClicked() {
        markReviewClickedThisSession(this.state.userSettings.active_realm_id);
        this.setState({reviewClicked: true});
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.stopWaitingForCrisp();
    }

    stopWaitingForCrisp() {
        clearInterval(this.crispPoll);
        clearTimeout(this.crispTimeout);
    }

    // Crisp is ready (or we've stopped waiting): read daf once and show step 4.
    crispSessionSettled() {
        this.stopWaitingForCrisp();
        if (!this.unmounted) {
            this.setState({crispSessionKnown: true, crispSaysReviewed: crispSaysReviewed()});
        }
    }

    // Sends the "I have a problem" message to our Crisp inbox as the visitor, then opens the chat so they can explain.
    reportProblem() {
        try {
            if (!window.$crisp) {
                throw new Error("Crisp is not loaded");
            }
            window.$crisp.push(["do", "message:send", ["text", REVIEW_PROBLEM_MESSAGE]]);
            window.$crisp.push(["do", "chat:open"]);
            this.setState({problemReported: true});
        } catch (ex) {
            showGenericErrorToast();
        }
    }

    componentDidMount() {
        // Crisp usually finishes loading after this page renders: wait for its session (event, or polling in case the event
        // already fired), and give up after CRISP_WAIT_MS so a blocked Crisp never hides the step for good.
        if (!this.state.crispSessionKnown) {
            try {
                if (window.$crisp) {
                    window.$crisp.push(["on", "session:loaded", () => this.crispSessionSettled()]);
                }
            } catch (ex) {
                // no Crisp: the poll/timeout below still settle it
            }
            this.crispPoll = setInterval(() => {
                if (crispSessionLoaded()) {
                    this.crispSessionSettled();
                }
            }, 250);
            this.crispTimeout = setTimeout(() => this.crispSessionSettled(), CRISP_WAIT_MS);
        }

        let url = new URL(window.location.href);
        let showMessage = url.searchParams.get('showMessage');
        if (showMessage === "1") {
            swalInstance.fire({icon: "success", title: "Plan Activated Successfully", text: "You can now access and use your new plan's features", confirmButtonColor: "#224514"});
        } else if (showMessage === "2") {
            swalInstance.fire({icon: "success", title: "Plan Reactivated Successfully", text: "You can now access and use your plan's features again", confirmButtonColor: "#224514"});
        }

        Promise.all([
            axios.get(window.SERVER_URL + '/get_settings'),
            axios.get(window.SERVER_URL + '/get_qr_invoices?index=1')
        ]).then(([res1, res2]) => {
            const settings = res1.data.result;
            const invoices = res2.data.result;
            this.setState({
                isLoading: false,
                userSettings: settings,
                remainingQrInvoicesForThisMonth: settings.remaining_qr_invoices_this_month,
                planMaxQrInvoicesForMonth: settings.plan_max_qr_invoices_per_month,
                planName: settings.plan_name,
                totalEntries: invoices.total_entries,
                qrTypeBreakdown: invoices.qr_type_breakdown || [],
            });
        });
    }

    dismissOnboardingModal() {
        localStorage.setItem("onboardingModalDismissed", "true");
        this.setState({showOnboardingModal: false})
    }

    render() {
        if (this.state.isLoading) {
            return <LoadingScreen />;
        }

        const usagePercent = this.state.planMaxQrInvoicesForMonth > 0
            ? Math.min(100, Math.round(((this.state.planMaxQrInvoicesForMonth - this.state.remainingQrInvoicesForThisMonth) / this.state.planMaxQrInvoicesForMonth) * 100))
            : 0;

        const hasQuickBooks = isQuickBooksConnected(this.state.userSettings);
        const hasBusinessDetails = !!this.state.userSettings.company_name;
        const hasFirstInvoice = this.state.totalEntries > 0;
        // A failed payment freezes the company: no new QR invoices until the card is updated.
        const isFrozen = this.state.userSettings.is_active === false;
        const reviewedInDatabase = this.state.userSettings.has_left_review === true;
        const reviewClicked = this.state.reviewClicked || reviewClickedThisSession(this.state.userSettings.active_realm_id);
        const hasLeftReview = reviewedInDatabase || reviewClicked || this.state.crispSaysReviewed;
        // Hidden until Crisp's session is known, unless the database or this session already say it's done.
        const showReviewStep = this.state.crispSessionKnown || reviewedInDatabase || reviewClicked;
        const stepsCompleted = [hasQuickBooks, hasBusinessDetails, hasFirstInvoice, hasLeftReview].filter(Boolean).length;

        const steps = [
            {
                title: "Connect to QuickBooks",
                desc: "Link your QuickBooks account to keep invoices and payments in sync automatically.",
                complete: hasQuickBooks,
                action: !hasQuickBooks ? <a className="btn btn-success" href="/oauth/init">Connect to QuickBooks</a> : null,
            },
            {
                title: "Add your business details",
                desc: "Fill in your company name and tax details so your QR invoices look right.",
                complete: hasBusinessDetails,
                action: (!hasBusinessDetails && hasQuickBooks) ? <a className="btn btn-outline-success" href="javascript:void(0)" onClick={() => this.props.history.push('/settings')}>Add business details</a> : null,
            },
            {
                title: "Create your first QR invoice",
                desc: "Generate a QR code for one of your QuickBooks invoices.",
                complete: hasFirstInvoice,
                action: (!hasFirstInvoice && hasQuickBooks && !isFrozen) ? <a className="btn btn-outline-success" href="javascript:void(0)" onClick={() => this.props.history.push('/new-qr')}>Create your first QR invoice</a> : null,
            },
            {
                title: "What do you think about Scan2Pay?",
                desc: hasLeftReview ? "Share your opinion about Scan2Pay." : "",
                complete: hasLeftReview,
                action: (!hasLeftReview && hasFirstInvoice) ? (
                    <div className="setup-guide-review-buttons">
                        <button className="btn btn-danger" onClick={this.reportProblem} disabled={this.state.problemReported}>
                            {this.state.problemReported ? "Message sent ✓" : <span>I have a problem <span aria-hidden="true">🙏</span></span>}
                        </button>
                        <a className="btn btn-primary" href={APP_STORE_REVIEW_URL} target="_blank" rel="noopener noreferrer" onClick={this.markReviewClicked}>
                            Amazing! <span aria-hidden="true">🤩</span>
                        </a>
                    </div>
                ) : null,
            },
        ].filter((step, index) => index < 3 || showReviewStep);

        let firstPendingSeen = false;

        return (
            <div>
                {/* TODO: re-enable once we have a new onboarding video link to embed
                <SimpleModal wide show={this.state.showOnboardingModal} onHide={this.dismissOnboardingModal} title="Welcome to Scan2Pay!">
                    <div style={{textAlign: "center"}}>
                        <span style={{fontSize: "18px"}}><b>Watch this video in order to learn how to add a QR Code onto your QuickBooks invoices:</b></span><br/><br/>
                        <iframe width="100%" height="315" src="https://www.youtube.com/embed/3LAG-fdWzbc" title="YouTube video player" frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                    </div>
                </SimpleModal>
                */}
                {/* Asked on every dashboard visit until they rate us (any star ends it for this browser session), but only once the
                    company has generated a QR invoice: someone who just installed the app has nothing to rate yet */}
                {hasFirstInvoice && isEligibleForReviewPrompt(this.state.userSettings) ? (
                    <ReviewPromptModal source="dashboard" realmId={this.state.userSettings.active_realm_id} onFiveStars={this.markReviewClicked} />
                ) : null}

                <h2 style={{marginBottom: "24px"}}>Dashboard</h2>

                {isFrozen ? <FrozenSubscriptionBanner userSettings={this.state.userSettings} style={{marginBottom: "24px"}} /> : null}

                <div className="setup-guide-card">
                    <div style={{flex: "1 1 auto", minWidth: 0}}>
                        <div style={{fontWeight: 700, fontSize: "1.1rem"}}>Setup guide</div>
                        <div style={{color: "#8892a0", marginBottom: "20px"}}>{stepsCompleted} of {SETUP_STEP_COUNT} completed</div>
                        {steps.map((step, index) => {
                            let statusClass = "is-pending";
                            if (step.complete) {
                                statusClass = "is-complete";
                            } else if (!firstPendingSeen) {
                                statusClass = "is-current";
                                firstPendingSeen = true;
                            }
                            return (
                                <div className={"setup-guide-step " + statusClass} key={step.title}>
                                    <div className="setup-guide-step-number">{step.complete ? "✓" : index + 1}</div>
                                    <div>
                                        <div className="setup-guide-step-title">{step.title}</div>
                                        {step.desc ? <div className="setup-guide-step-desc">{step.desc}</div> : null}
                                        {statusClass === "is-current" && step.action ? <div style={{marginTop: "10px"}}>{step.action}</div> : null}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <img src={setupGuideIllustration} alt="" className="setup-guide-illustration" />
                </div>

                <div className="stat-card-row">
                    <div className="stat-card">
                        <div className="stat-card-icon"><GridIcon size={20} /></div>
                        <div className="stat-card-label">Total QR Invoices</div>
                        <div className="stat-card-value" style={{marginTop: "12px"}}>{this.state.totalEntries}</div>
                        <div style={{color: "#8892a0", fontSize: "0.85rem"}}>In all time</div>
                        <div style={{marginTop: "12px"}}><a href="javascript:void(0)" onClick={() => this.props.history.push('/all-invoices')}>View all invoices</a></div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-card-icon">$</div>
                        <div className="stat-card-label">Plan & Usage</div>
                        <div style={{display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px"}}>
                            <span className="badge" style={{background: "#eef1f4", color: "#232b3d", padding: "4px 10px", borderRadius: "6px", whiteSpace: "nowrap"}}>{this.state.planName}</span>
                            <a href="javascript:void(0)" onClick={() => this.props.history.push('/subscriptions')}>Change Plan</a>
                        </div>
                        {this.state.userSettings.first_day_next_month ?
                            <div style={{color: "#8892a0", fontSize: "0.85rem", marginTop: "8px"}}>Billing period ends {this.state.userSettings.first_day_next_month}</div> : null}
                        <div className="stat-card-value" style={{marginTop: "8px"}}>
                            {this.state.planMaxQrInvoicesForMonth - this.state.remainingQrInvoicesForThisMonth} / {this.state.planMaxQrInvoicesForMonth}
                        </div>
                        <div className="usage-progress-track">
                            <div className="usage-progress-fill" style={{width: usagePercent + "%"}}></div>
                        </div>
                        <div style={{color: "#8892a0", fontSize: "0.85rem"}}>QR invoices left until {this.state.userSettings.first_day_next_month}</div>
                    </div>

                    <div className="stat-card stat-card-chart">
                        <div style={{fontWeight: 700}}>QR Type Breakdown</div>
                        <div style={{color: "#8892a0", fontSize: "0.85rem", marginBottom: "16px"}}>By type generated</div>
                        <Donut segments={this.state.qrTypeBreakdown} />
                    </div>
                </div>
            </div>
        );
    }
}

export default Home;
